import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

type Accent = 'indigo' | 'orange'

interface FeaturedItem {
  name: string
  description: string
  url: string
  /** External product site (true) vs. GitHub repo (false). */
  external: boolean
  accent: Accent
  icon: ReactNode
  tags: string[]
}

const accentStyles: Record<Accent, { iconWrap: string; tag: string; link: string }> = {
  indigo: {
    iconWrap: 'bg-indigo-100 text-indigo-600',
    tag: 'bg-indigo-50 text-indigo-700',
    link: 'text-indigo-700 hover:text-indigo-800',
  },
  orange: {
    iconWrap: 'bg-orange-100 text-orange-600',
    tag: 'bg-orange-50 text-orange-700',
    link: 'text-orange-700 hover:text-orange-800',
  },
}

const githubIcon = (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
)

export function FeaturedProducts() {
  const { t } = useTranslation('home')

  const featuredItems: FeaturedItem[] = [
    {
      name: t('featured.whichModel.name'),
      description: t('featured.whichModel.description'),
      url: 'https://which-model.akasan-ml.tech/',
      external: true,
      accent: 'indigo',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      tags: [
        t('featured.whichModel.tags.game'),
        t('featured.whichModel.tags.llm'),
        t('featured.whichModel.tags.quiz'),
      ],
    },
    {
      name: t('featured.llmUsages.name'),
      description: t('featured.llmUsages.description'),
      url: 'https://github.com/Akasan/llm_usages',
      external: false,
      accent: 'orange',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      tags: [
        t('featured.llmUsages.tags.rust'),
        t('featured.llmUsages.tags.cli'),
        t('featured.llmUsages.tags.llm'),
      ],
    },
  ]

  return (
    <section className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            {t('featured.sectionTitle')}
          </h2>
          <p className="text-gray-600">{t('featured.sectionDescription')}</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredItems.map((item) => {
            const styles = accentStyles[item.accent]
            return (
              <div
                key={item.name}
                className="bg-white rounded-2xl p-6 hover:shadow-lg transition-shadow border border-gray-200"
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 ${styles.iconWrap}`}>
                  {item.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.name}</h3>
                <p className="text-gray-600 text-sm mb-4">{item.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {item.tags.map((tag) => (
                    <span
                      key={tag}
                      className={`px-3 py-1 text-xs rounded-full ${styles.tag}`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-2 text-sm font-medium ${styles.link}`}
                >
                  {item.external ? null : githubIcon}
                  {item.external ? t('featured.visitSite') : t('featured.viewOnGithub')}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            )
          })}
        </div>
        <div className="text-center mt-8">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors duration-200 text-sm font-medium"
          >
            {t('featured.viewAllProducts')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  )
}
